import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaLock, FaTrashAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const validatePassword = (pass) => {
  if (pass.length < 6) return "Password must be at least 6 characters.";
  if (!/[A-Z]/.test(pass)) return "Password must include at least one uppercase letter.";
  if (!/[a-z]/.test(pass)) return "Password must include at least one lowercase letter.";
  if (!/[0-9]/.test(pass)) return "Password must include at least one number.";
  if (!/[^A-Za-z0-9]/.test(pass)) return "Password must include at least one special character.";
  return "";
};

const AccountSecurity = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [cpStep, setCpStep] = useState(1);
  const [cpEmail, setCpEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [enteredCode, setEnteredCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [reNewPass, setReNewPass] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showPasswords, setShowPasswords] = useState({ new: false, confirm: false });
  const [codeExpiry, setCodeExpiry] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [deletedMessage, setDeletedMessage] = useState("");
  const [isAmbassador, setIsAmbassador] = useState(false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine whether the user is an Ambassador (has an AP and/or IC code) or a Free Explorer
  useEffect(() => {
    const userID = localStorage.getItem("userID");
    const userName = localStorage.getItem("username");
    if (!userID) return;

    const fetchProfile = async () => {
      try {
        const response = await fetch(
          "https://xianhefairy.com/LotusFairy_API/api/User/GetProfile",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              UserName: userName,
              ID: userID,
              UserToken: "",
              Token: {
                AccessToken:
                  "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
              },
            }),
          },
        );

        const result = await response.json();
        const profile = result?.Profiles?.[0];
        if (response.ok && profile) {
          setIsAmbassador(Boolean(profile.VP_CODE || profile.VVP_CODE));
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    if (codeExpiry === 0) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((codeExpiry - Date.now()) / 1000));
      setTimeLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [codeExpiry]);

  const formatTimeLeft = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const resetChangePasswordState = () => {
    setShowChangePasswordModal(false);
    setCpStep(1);
    setCpEmail("");
    setVerificationCode("");
    setEnteredCode("");
    setNewPass("");
    setReNewPass("");
    setErrorMsg("");
    setShowPasswords({ new: false, confirm: false });
    setCodeExpiry(0);
    setTimeLeft(0);
  };

  const handleSendEmail = async () => {
    if (!cpEmail.trim()) {
      setErrorMsg(t("auth.enterEmail", { defaultValue: "Please enter your email." }));
      return;
    }
    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/email/send_reset",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ToEmail: cpEmail.trim(), CreatedBy: cpEmail.trim() }),
        }
      );
      const result = await res.json();
      if (result) {
        setVerificationCode(result.sentCode);
        setErrorMsg("");
        const expiresAt = Date.now() + 10 * 60 * 1000;
        setCodeExpiry(expiresAt);
        setTimeLeft(10 * 60);
        setCpStep(2);
      } else {
        setErrorMsg(result?.Message || t("auth.resetPassword", { defaultValue: "Failed to send reset code." }));
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Error sending reset email.");
    }
  };

  const handleVerifyCode = () => {
    if (Date.now() > codeExpiry) {
      setErrorMsg("Verification code has expired. Please request a new one.");
      return;
    }
    if (String(enteredCode) === String(verificationCode)) {
      setErrorMsg("");
      setCpStep(3);
    } else {
      setErrorMsg(t("auth.resetPassword", { defaultValue: "Invalid verification code." }));
    }
  };

  const handleChangePassword = async () => {
    const passError = validatePassword(newPass);
    if (passError) {
      setErrorMsg(passError);
      return;
    }
    if (newPass !== reNewPass) {
      setErrorMsg(t("accountSecurity.passwordMismatch", { defaultValue: "Passwords do not match." }));
      return;
    }
    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/booking/ResetPassword",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Username: cpEmail.trim(), NewPassword: newPass }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.Success === true) {
        resetChangePasswordState();
      } else {
        setErrorMsg(result?.Message || t("accountInfo.failChangePass", { defaultValue: "Failed to change password." }));
      }
    } catch (error) {
      console.error(error);
      setErrorMsg(t("accountInfo.errorChangePass", { defaultValue: "Error changing password." }));
    }
  };

  const resetDeleteState = () => {
    setShowDeleteModal(false);
    setDeletePassword("");
    setDeleteError("");
  };

  const handleCloseDeleteModal = () => {
    resetDeleteState();
  };

  const handleDeletedModalConfirm = () => {
    setShowDeletedModal(false);
    localStorage.clear();
    navigate("/main", { replace: true });
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");
    if (!deletePassword.trim()) {
      setDeleteError(t("accountInfo.deleteError", { defaultValue: "Please enter your password." }));
      return;
    }

    const safeParseJSON = (value) => {
      try {
        return JSON.parse(value || "{}");
      } catch {
        return {};
      }
    };

    const userDetails = safeParseJSON(localStorage.getItem("userData"));
    const custId = String(
      localStorage.getItem("userID") ||
      localStorage.getItem("userId") ||
      userDetails?.ID ||
      userDetails?.Id ||
      userDetails?.userID ||
      userDetails?.userId ||
      userDetails?.CustId ||
      userDetails?.custId ||
      "",
    ).trim();

    if (!custId) {
      setDeleteError(
        t("accountInfo.userIdNotFound", {
          defaultValue: "User ID not found. Please log in again.",
        }),
      );
      return;
    }

    setIsDeleting(true);
    try {
      const url = `https://xianhefairy.com/LotusFairy_API/api/User/DeleteAccount?CustId=${encodeURIComponent(custId)}&Password=${encodeURIComponent(deletePassword.trim())}`;
      const res = await fetch(url, { method: "POST" });
      const result = await res.json();
      if (result && result.successful === true) {
        setDeletedMessage(
          result.Message ||
            t("accountInfo.accountDeletedMsg", { defaultValue: "Your account has been deleted." })
        );
        handleCloseDeleteModal();
        setShowDeletedModal(true);
      } else {
        setDeleteError(
          result?.Message ||
            t("accountInfo.accountDeleteFail", { defaultValue: "Failed to delete account." })
        );
      }
    } catch (error) {
      console.error(error);
      setDeleteError(
        t("accountInfo.accountDeleteError", { defaultValue: "An error occurred. Please try again." })
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: "#0A031E",
    border: "1px solid rgba(201,168,75,0.4)",
    borderRadius: "10px",
    color: "#fff2d2",
    padding: "11px 40px 11px 13px",
    fontSize: "15px",
    fontFamily: "DMSans, sans-serif",
    outline: "none",
    boxSizing: "border-box",
  };

  const fieldWrapStyle = { position: "relative" };

  const eyeButtonStyle = {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "#deaf77",
    cursor: "pointer",
    padding: 0,
    display: "grid",
    placeItems: "center",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: isMobile ? "20px 14px 120px" : "34px 20px 160px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "680px",
          margin: "0 auto",
          borderRadius: "18px",
          border: "2px solid rgba(201,168,75,0.4)",
          background: "#0A031E",
          boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
          padding: isMobile ? "18px" : "24px",
          fontFamily: "DMSans, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
            gap: "8px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            aria-label={t("account.back", { defaultValue: "Back" })}
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              border: "1px solid rgba(201,168,75,0.5)",
              background: "transparent",
              color: "#deaf77",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <FaChevronLeft size={14} />
          </button>

          <h1
            style={{
              margin: 0,
              color: "#deaf77",
              fontFamily: "DMSans-Bold, DMSans, sans-serif",
              fontSize: isMobile ? "24px" : "28px",
            }}
          >
            {t("account.accountSecurity", { defaultValue: "Account Security" })}
          </h1>
        </div>

        {/* Change Password */}
        <button
          onClick={() => setShowChangePasswordModal(true)}
          style={{
            width: "100%",
            borderRadius: "14px",
            border: "1px solid rgba(201,168,75,0.4)",
            background: "#0A031E",
            color: "#fff2d2",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "14px" : "15px 16px",
            cursor: "pointer",
            marginBottom: "24px",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <span
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "10px",
                background: "rgba(201,168,75,0.12)",
                border: "1px solid rgba(201,168,75,0.35)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <FaLock size={16} color="#deaf77" />
            </span>
            <span style={{ textAlign: "left" }}>
              <div
                style={{
                  fontFamily: "DMSans-Bold, DMSans, sans-serif",
                  fontSize: isMobile ? "15px" : "16px",
                  color: "#fff2d2",
                }}
              >
                {t("security.changePassword", { defaultValue: "Change Password" })}
              </div>
              <div style={{ fontSize: "12px", color: "#D8CCA8", marginTop: "2px" }}>
                {t("security.changePasswordDesc", {
                  defaultValue: "Update your password regularly to keep your account protected.",
                })}
              </div>
            </span>
          </span>
          <FaChevronRight size={13} color="#deaf77" style={{ flexShrink: 0 }} />
        </button>

        {/* Danger Zone */}
        <div>
          <p
            style={{
              margin: "0 0 10px 2px",
              fontFamily: "DMSans-Bold, DMSans, sans-serif",
              fontSize: "13px",
              color: "#FF665E",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {t("security.dangerZone", { defaultValue: "Danger Zone" })}
          </p>

          <button
            onClick={() => setShowDeleteModal(true)}
            style={{
              width: "100%",
              borderRadius: "14px",
              border: "1px solid rgba(255,95,95,0.5)",
              background: "rgba(102,17,24,0.18)",
              color: "#fff2d2",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: isMobile ? "14px" : "15px 16px",
              cursor: "pointer",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <span
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  background: "rgba(255,95,95,0.12)",
                  border: "1px solid rgba(255,95,95,0.35)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <FaTrashAlt size={16} color="#FF665E" />
              </span>
              <span style={{ textAlign: "left" }}>
                <div
                  style={{
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: isMobile ? "15px" : "16px",
                    color: "#FF665E",
                  }}
                >
                  {t("security.deleteAccount", { defaultValue: "Delete Account" })}
                </div>
                <div style={{ fontSize: "12px", color: "#D8CCA8", marginTop: "2px" }}>
                  {t("security.deleteAccountDesc", {
                    defaultValue: "Permanently remove your account and all linked data.",
                  })}
                </div>
              </span>
            </span>
            <FaChevronRight size={13} color="#FF665E" style={{ flexShrink: 0 }} />
          </button>
        </div>
      </div>

      {/* ── Change Password Modal ── */}
      {showChangePasswordModal && (
        <div
          onClick={resetChangePasswordState}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "grid",
            placeItems: "center",
            padding: "14px",
            zIndex: 2500,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "14px",
              border: "1px solid rgba(201,168,75,0.4)",
              background: "#171520",
              padding: "20px",
              color: "#fff2d2",
              fontFamily: "DMSans, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
              <h2 style={{ margin: 0, fontFamily: "DMSans-Bold, DMSans, sans-serif", fontSize: "20px" }}>
                {t("security.changePassword", { defaultValue: "Change Password" })}
              </h2>
              <button
                onClick={resetChangePasswordState}
                style={{ border: "none", background: "transparent", color: "#deaf77", cursor: "pointer", fontSize: "14px" }}
              >
                {t("account.close", { defaultValue: "Close" })}
              </button>
            </div>

            {/* Step 1: Enter Email */}
            {cpStep === 1 && (
              <div>
                <label style={{ fontSize: "13px", color: "#D8CCA8", display: "block", marginBottom: "6px" }}>
                  {t("auth.email", { defaultValue: "Email" })}
                </label>
                <input
                  type="email"
                  value={cpEmail}
                  onChange={(e) => setCpEmail(e.target.value)}
                  style={inputStyle}
                  autoComplete="email"
                  placeholder="Enter your email"
                />
                {errorMsg && (
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#FF665E" }}>{errorMsg}</p>
                )}
                <button
                  onClick={handleSendEmail}
                  style={{
                    marginTop: "18px",
                    width: "100%",
                    borderRadius: "999px",
                    border: "none",
                    background: "linear-gradient(90deg, #C9A84B, #deaf77)",
                    color: "#0A031E",
                    padding: "13px",
                    cursor: "pointer",
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: "15px",
                  }}
                >
                  {t("security.sendCode", { defaultValue: "Send Code" })}
                </button>
              </div>
            )}

            {/* Step 2: Enter Verification Code */}
            {cpStep === 2 && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <label style={{ fontSize: "13px", color: "#D8CCA8" }}>
                    {t("security.verificationCode", { defaultValue: "Verification Code" })}
                  </label>
                  <span style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: timeLeft > 0 ? "#deaf77" : "#FF665E",
                  }}>
                    {timeLeft > 0 ? `⏱ ${formatTimeLeft(timeLeft)}` : "⚠ Expired"}
                  </span>
                </div>
                <input
                  type="text"
                  value={enteredCode}
                  onChange={(e) => setEnteredCode(e.target.value)}
                  style={{ ...inputStyle, opacity: timeLeft === 0 ? 0.5 : 1 }}
                  autoComplete="one-time-code"
                  placeholder="Enter the code sent to your email"
                  disabled={timeLeft === 0}
                />
                {errorMsg && (
                  <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#FF665E" }}>{errorMsg}</p>
                )}
                <button
                  onClick={handleVerifyCode}
                  disabled={timeLeft === 0}
                  style={{
                    marginTop: "18px",
                    width: "100%",
                    borderRadius: "999px",
                    border: "none",
                    background: timeLeft === 0
                      ? "rgba(201,168,75,0.3)"
                      : "linear-gradient(90deg, #C9A84B, #deaf77)",
                    color: "#0A031E",
                    padding: "13px",
                    cursor: timeLeft === 0 ? "not-allowed" : "pointer",
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: "15px",
                    opacity: timeLeft === 0 ? 0.5 : 1,
                  }}
                >
                  {t("security.verifyCode", { defaultValue: "Verify Code" })}
                </button>
                {timeLeft === 0 && (
                  <button
                    onClick={() => { setCpStep(1); setEnteredCode(""); setErrorMsg(""); }}
                    style={{
                      marginTop: "10px",
                      width: "100%",
                      borderRadius: "999px",
                      border: "1px solid rgba(201,168,75,0.5)",
                      background: "transparent",
                      color: "#deaf77",
                      padding: "13px",
                      cursor: "pointer",
                      fontFamily: "DMSans-Bold, DMSans, sans-serif",
                      fontSize: "15px",
                    }}
                  >
                    {t("security.resendCode", { defaultValue: "Resend Code" })}
                  </button>
                )}
              </div>
            )}

            {/* Step 3: Enter New Password */}
            {cpStep === 3 && (
              <div style={{ display: "grid", gap: "12px" }}>
                {[
                  { key: "new", label: t("security.newPassword", { defaultValue: "New Password" }), value: newPass, setter: setNewPass },
                  { key: "confirm", label: t("security.confirmPassword", { defaultValue: "Confirm New Password" }), value: reNewPass, setter: setReNewPass },
                ].map(({ key, label, value, setter }) => (
                  <div key={key}>
                    <label style={{ fontSize: "13px", color: "#D8CCA8", display: "block", marginBottom: "6px" }}>
                      {label}
                    </label>
                    <div style={fieldWrapStyle}>
                      <input
                        type={showPasswords[key] ? "text" : "password"}
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                        style={inputStyle}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }))}
                        style={eyeButtonStyle}
                        aria-label={showPasswords[key] ? "Hide password" : "Show password"}
                      >
                        {showPasswords[key] ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                      </button>
                    </div>
                  </div>
                ))}
                {errorMsg && (
                  <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#FF665E" }}>{errorMsg}</p>
                )}
                <button
                  onClick={handleChangePassword}
                  style={{
                    marginTop: "6px",
                    width: "100%",
                    borderRadius: "999px",
                    border: "none",
                    background: "linear-gradient(90deg, #C9A84B, #deaf77)",
                    color: "#0A031E",
                    padding: "13px",
                    cursor: "pointer",
                    fontFamily: "DMSans-Bold, DMSans, sans-serif",
                    fontSize: "15px",
                  }}
                >
                  {t("security.savePassword", { defaultValue: "Save Password" })}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div
          onClick={handleCloseDeleteModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.78)",
            display: "grid",
            placeItems: "center",
            padding: "14px",
            zIndex: 2500,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "14px",
              border: "1px solid rgba(255,95,95,0.45)",
              background: "#171520",
              padding: "20px",
              color: "#fff2d2",
              fontFamily: "DMSans, sans-serif",
            }}
          >
            <div
              style={{
                width: "46px",
                height: "46px",
                borderRadius: "50%",
                background: "rgba(255,95,95,0.14)",
                border: "1px solid rgba(255,95,95,0.4)",
                display: "grid",
                placeItems: "center",
                marginBottom: "14px",
              }}
            >
              <FaTrashAlt size={18} color="#FF665E" />
            </div>

            <h2 style={{ margin: "0 0 6px", fontFamily: "DMSans-Bold, DMSans, sans-serif", fontSize: "20px", color: "#FF665E" }}>
              {t("security.deleteAccount", { defaultValue: "Delete Account" })}
            </h2>
            <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#D8CCA8", lineHeight: 1.5 }}>
              {isAmbassador
                ? t("security.deleteWarningAmbassador", {
                    defaultValue:
                      "This action is permanent and cannot be undone. Your unused Gems, digital purchases (including any active A-MU, digital courses, and unused draws), data, and history will all be permanently removed and are non-refundable — including your downline and any commissions, whether unused or not yet drawn.",
                  })
                : t("security.deleteWarningFreeExplorer", {
                    defaultValue:
                      "This action is permanent and cannot be undone. Your unused Gems, digital purchases (including any active A-MU, digital courses, and unused draws), data, and history will all be permanently removed and are non-refundable.",
                  })}
            </p>

            <label style={{ fontSize: "13px", color: "#D8CCA8", display: "block", marginBottom: "6px" }}>
              {t("security.enterPasswordToConfirm", { defaultValue: "Enter your password to confirm" })}
            </label>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="••••••••"
              style={{ ...inputStyle, border: "1px solid rgba(255,95,95,0.45)" }}
              autoComplete="current-password"
            />

            {deleteError && (
              <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#FF665E" }}>{deleteError}</p>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "18px" }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  borderRadius: "999px",
                  border: "1px solid rgba(201,168,75,0.45)",
                  background: "transparent",
                  color: "#deaf77",
                  padding: "12px",
                  cursor: "pointer",
                  fontFamily: "DMSans, sans-serif",
                  fontSize: "14px",
                }}
              >
                {t("account.cancel", { defaultValue: "Cancel" })}
              </button>

              <button
                onClick={handleDeleteAccount}
                disabled={!deletePassword.trim() || isDeleting}
                style={{
                  borderRadius: "999px",
                  border: "none",
                  background:
                    deletePassword.trim() && !isDeleting
                      ? "linear-gradient(90deg, rgba(153,25,36,0.9), rgba(204,30,45,0.85))"
                      : "rgba(102,17,24,0.3)",
                  color: deletePassword.trim() && !isDeleting ? "#fff" : "#888",
                  padding: "12px",
                  cursor: deletePassword.trim() && !isDeleting ? "pointer" : "not-allowed",
                  fontFamily: "DMSans-Bold, DMSans, sans-serif",
                  fontSize: "14px",
                  transition: "background 0.2s",
                }}
              >
                {isDeleting
                  ? t("security.deleting", { defaultValue: "Deleting..." })
                  : t("security.confirmDelete", { defaultValue: "Delete Account" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Account Deleted Modal ── */}
      {showDeletedModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.78)",
            display: "grid",
            placeItems: "center",
            padding: "14px",
            zIndex: 2500,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "380px",
              borderRadius: "14px",
              border: "1px solid rgba(201,168,75,0.4)",
              background: "#171520",
              padding: "24px 20px",
              color: "#fff2d2",
              fontFamily: "DMSans, sans-serif",
              textAlign: "center",
            }}
          >
            <h2 style={{ margin: "0 0 10px", fontFamily: "DMSans-Bold, DMSans, sans-serif", fontSize: "20px", color: "#deaf77" }}>
              {t("accountInfo.accountDeleted", { defaultValue: "Account Deleted" })}
            </h2>
            <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#D8CCA8", lineHeight: 1.5 }}>
              {deletedMessage}
            </p>
            <button
              onClick={handleDeletedModalConfirm}
              style={{
                width: "100%",
                borderRadius: "999px",
                border: "none",
                background: "linear-gradient(90deg, #C9A84B, #deaf77)",
                color: "#0A031E",
                padding: "13px",
                cursor: "pointer",
                fontFamily: "DMSans-Bold, DMSans, sans-serif",
                fontSize: "15px",
              }}
            >
              {t("account.ok", { defaultValue: "OK" })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSecurity;
